(() => {
  const t = (i18n, key, fallback) =>
    i18n && i18n[key] != null && String(i18n[key]).trim() !== '' && !String(i18n[key]).startsWith('translation missing')
      ? i18n[key]
      : fallback;

  const cartAddUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}cart/add.js`;
  };

  const norm = (s) =>
    String(s || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/×/g, 'x')
      .replace(/,/g, '.')
      .replace(/\s+/g, ' ')
      .trim();

  const includesAny = (hay, needles) => {
    if (!needles?.length) return false;
    const h = norm(hay);
    return needles.some((n) => h.includes(norm(n)));
  };

  const excludesAny = (hay, needles) => {
    if (!needles?.length) return true;
    const h = norm(hay);
    return !needles.some((n) => h.includes(norm(n)));
  };

  const formatMoney = (cents) => {
    if (cents == null || cents === '') return '—';
    const n = Number(cents);
    if (Number.isNaN(n)) return '—';
    return `${Math.round(n / 100).toLocaleString('tr-TR')} ₺`;
  };

  const moneyOf = (variant) => (variant ? formatMoney(variant.price) : '—');

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

  const notifyHorizonCart = async (addResponse, lines) => {
    let cartJson = null;
    try {
      cartJson = await fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
    } catch (_) {}
    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines || [],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: cartJson ? CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson) : undefined,
        detail: {
          items: cartJson?.items || [],
          source: 'hangbox-craft',
          itemCount: cartJson?.item_count,
          sections: addResponse?.sections || {},
          didError: false,
        },
      });
    } catch (_) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cartJson || addResponse, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartJson || addResponse, bubbles: true }));
    }
  };

  class HangboxCraft extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const cfgEl = document.getElementById(this.dataset.configId || '');
      if (!cfgEl) return;
      try {
        this.config = JSON.parse(cfgEl.textContent);
      } catch (e) {
        console.error('[HangboxCraft] bad config', e);
        return;
      }

      this.i18n = this.config.i18n || {};
      this.variants = this.config.variants || [];
      const allOptions = this.config.options || [];
      this.options = allOptions.filter((opt) => {
        const base = this.variants.find(
          (v) => includesAny(v.title, opt.baseContains) && excludesAny(v.title, opt.baseExcludes || [])
        );
        return Boolean(base);
      });
      if (!this.options.length) this.options = allOptions;
      this.moneyFormat = this.config.moneyFormat || '';
      this.orientId = this.options[0]?.orient || 'yatay';
      this.optionId = this.options.find((o) => o.orient === this.orientId)?.id || this.options[0]?.id || null;
      this.wantPrint = false;
      this.fileFront = null;
      this.fileBack = null;
      this._frontUrl = null;
      this._backUrl = null;

      this._cacheDom();
      this._initPrintSource();
      this._renderOrientPills();
      this._renderSizePills();
      this._bind();
      this._placePrintAfterCtas();
      this._markHosts();
      this._interceptCart();
      this._sync();
    }

    disconnectedCallback() {
      if (this._frontUrl) URL.revokeObjectURL(this._frontUrl);
      if (this._backUrl) URL.revokeObjectURL(this._backUrl);
    }

    _cacheDom() {
      this.orientPills = this.querySelector('[data-hb-orients]');
      this.sizePills = this.querySelector('[data-hb-sizes]');
      this.printBtn = this.querySelector('[data-hb-print-btn]');
      this.printMeta = this.querySelector('[data-hb-print-meta]');
      this.printPriceEl = this.querySelector('[data-hb-print-price]');
      this.uploadEl = this.querySelector('[data-hb-upload]');
      this.printGuideEl = this.querySelector('[data-hb-print-guide]');
      this.fileFrontInput = this.querySelector('[data-hb-file-front]');
      this.fileBackInput = this.querySelector('[data-hb-file-back]');
      this.fileFrontName = this.querySelector('[data-hb-file-front-name]');
      this.fileBackName = this.querySelector('[data-hb-file-back-name]');
      this.printWarning = this.querySelector('[data-hb-print-warning]');
      this.printStepEl = this.querySelector('[data-hb-print-step]');
      this.descEl = this.querySelector('[data-hb-desc]');
      this.basePriceEl = this.querySelector('[data-hb-base-price]');
      this.printRow = this.querySelector('[data-hb-print-row]');
      this.printLine = this.querySelector('[data-hb-print-line]');
      this.printStateEl = this.querySelector('[data-hb-print-state]');
      this.totalEl = this.querySelector('[data-hb-total]');
      this.statusEl = this.querySelector('[data-hb-status]');
    }

    _optionsForOrient() {
      return this.options.filter((o) => o.orient === this.orientId);
    }

    _renderOrientPills() {
      if (!this.orientPills) return;
      this.orientPills.innerHTML = '';
      const seen = new Set();
      const orients = [];
      this.options.forEach((opt) => {
        if (!opt.orient || seen.has(opt.orient)) return;
        seen.add(opt.orient);
        orients.push(opt);
      });
      const orientStep = this._orientStepEl();
      if (orients.length <= 1) {
        if (orientStep) orientStep.hidden = true;
        return;
      }
      if (orientStep) orientStep.hidden = false;
      orients.forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hangbox-craft__pill';
        btn.setAttribute('data-hb-orient', opt.orient);
        btn.setAttribute('aria-pressed', 'false');
        btn.innerHTML = `<span>${opt.orientLabel || opt.orient}</span>`;
        this.orientPills.appendChild(btn);
      });
    }

    _renderSizePills() {
      if (!this.sizePills) return;
      this.sizePills.innerHTML = '';
      this._optionsForOrient().forEach((opt) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hangbox-craft__pill';
        btn.setAttribute('data-hb-size', opt.id);
        btn.setAttribute('aria-pressed', 'false');
        btn.innerHTML = `<span>${opt.label}</span><span class="hangbox-craft__pill-meta">${opt.sizeLabel || ''}</span>`;
        this.sizePills.appendChild(btn);
      });
    }

    _bind() {
      this.orientPills?.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-hb-orient]');
        if (!btn) return;
        const nextOrient = btn.getAttribute('data-hb-orient');
        if (!nextOrient) return;
        const orientChanged = nextOrient !== this.orientId;
        const anchorTop = this._anchorTop(this._orientStepEl() || btn);
        this.orientId = nextOrient;
        // Yön değişince her zaman o gruptaki 1. ölçüyü seç
        if (orientChanged) {
          const first = this._optionsForOrient()[0];
          if (first) this.optionId = first.id;
        }
        this._renderSizePills();
        this._sync();
        if (orientChanged) this._restoreAnchor(this._orientStepEl() || btn, anchorTop);
      });
      this.sizePills?.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-hb-size]');
        if (!btn) return;
        const nextSize = btn.getAttribute('data-hb-size');
        if (!nextSize || nextSize === this.optionId) return;
        const anchorTop = this._anchorTop(this.sizePills || btn);
        this.optionId = nextSize;
        this._sync();
        this._restoreAnchor(this.sizePills || btn, anchorTop);
      });
      this.printBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const next = !this.wantPrint;
        this.wantPrint = next;
        if (!next) {
          this._setFront(null);
          this._setBack(null);
          this.printSrc?.reset();
          this._sync();
          return;
        }
        this._sync();
        this._openPrintUpload();
      });
      this._bindFileUploads();
    }

    _initPrintSource() {
      const pdp = window.HarfmixCraftPdp;
      this.printSrc =
        pdp && pdp.createPrintSource
          ? pdp.createPrintSource(this, 'hb', {
              onService: () => {
                this._setFront(null);
                this._setBack(null);
              },
              onChange: () => this._sync(),
            })
          : { source: 'upload', isService: false, noteValue: () => '', reset() {}, sync() {} };
    }

    /** Print add-on lives BELOW the buy buttons (same as standbox-craft.js). */
    _placePrintAfterCtas() {
      const step = this.printStepEl || this.printBtn?.closest('.craft-print-step');
      if (!step) return;
      const tryPlace = () => {
        const pdp = window.HarfmixCraftPdp;
        if (pdp && pdp.placePrintAfterCtas) {
          pdp.placePrintAfterCtas(this, step);
          return true;
        }
        return false;
      };
      if (tryPlace()) return;
      [50, 200, 600, 1500].forEach((ms) => setTimeout(tryPlace, ms));
    }

    _bindFileUploads() {
      const bindInput = (input, onFile) => {
        if (!input) return;
        input.addEventListener('change', () => onFile(input.files?.[0] || null));
        const drop = input.closest('label, .hangbox-craft__drop');
        if (!drop) return;
        ['dragenter', 'dragover'].forEach((ev) => {
          drop.addEventListener(ev, (e) => {
            e.preventDefault();
            e.stopPropagation();
            drop.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach((ev) => {
          drop.addEventListener(ev, (e) => {
            e.preventDefault();
            e.stopPropagation();
            drop.classList.remove('is-dragover');
          });
        });
        drop.addEventListener('drop', (e) => {
          const file = e.dataTransfer?.files?.[0];
          if (!file) return;
          try {
            const dt = new DataTransfer();
            dt.items.add(file);
            input.files = dt.files;
          } catch (_) {}
          onFile(file);
        });
      };

      bindInput(this.fileFrontInput, (file) => this._setFront(file));
      bindInput(this.fileBackInput, (file) => this._setBack(file));
    }

    _openPrintUpload({ scroll = true } = {}) {
      this.wantPrint = true;
      if (this.uploadEl) this.uploadEl.hidden = false;
      if (this.printBtn) this.printBtn.setAttribute('aria-pressed', 'true');
      if (scroll) {
        const target = this.uploadEl || this.printStepEl || this.printBtn?.closest('.craft-print-step') || this.printBtn;
        if (target) {
          requestAnimationFrame(() => {
            target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          });
        }
      }
      if (this.printSrc?.isService) {
        this.printSrc.noteEl?.focus?.({ preventScroll: true });
      } else {
        this.fileFrontInput?.focus?.({ preventScroll: true });
      }
    }

    _orientStepEl() {
      return (
        this.querySelector('[data-hb-orient-step]') ||
        this.orientPills?.closest('.hangbox-craft__step') ||
        this.orientPills ||
        null
      );
    }

    _anchorTop(el) {
      if (!el || !(el instanceof Element)) return null;
      return el.getBoundingClientRect().top;
    }

    _restoreAnchor(el, beforeTop) {
      if (beforeTop == null || !el || !(el instanceof Element)) return;
      const adjust = () => {
        const afterTop = el.getBoundingClientRect().top;
        const delta = afterTop - beforeTop;
        if (Math.abs(delta) < 1) return;
        // Önizleme yüksekliği değişince "Yön seçin" aynı yerde kalsın
        window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
      };
      adjust();
      requestAnimationFrame(() => {
        adjust();
        requestAnimationFrame(adjust);
      });
      // Aspect-ratio / font layout için kısa ikinci geçiş
      setTimeout(adjust, 50);
      setTimeout(adjust, 120);
    }

    _markHosts() {
      document.querySelectorAll('product-info, .product, .product-information').forEach((el) => {
        el.classList.add('product--hangboxcraft');
        const boot = () => { if (window.HarfmixCraftPdp) { window.HarfmixCraftPdp.init({ quoteOnly: false }); return true; } return false; };
        if (!boot()) [50, 200, 600, 1500].forEach((ms) => setTimeout(boot, ms));
      });
    }

    _interceptCart() {
      const isOurAtcButton = (el) => {
        if (!(el instanceof Element)) return false;
        const btn = el.closest('button[type="submit"], button[name="add"], [name="add"]');
        if (!btn || btn.closest('hangbox-craft')) return false;
        return (
          document.body.contains(this) &&
          !!btn.closest('sticky-add-to-cart, .sticky-add-to-cart, product-form, .product-form, form[action*="/cart/add"]')
        );
      };
      const guard = (event) => {
        if (event.type === 'submit') {
          const form = event.target instanceof HTMLFormElement ? event.target : event.target.closest?.('form');
          if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.action || '')) return;
          if (form.closest('hangbox-craft') || !document.body.contains(this)) return;
          event.preventDefault();
          event.stopPropagation();
          this._addToCart();
          return;
        }
        if (!isOurAtcButton(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        this._addToCart();
      };
      document.addEventListener('click', guard, true);
      document.addEventListener('submit', guard, true);
    }

    _findVariant(contains, excludes) {
      return (
        this.variants.find((v) => includesAny(v.title, contains) && excludesAny(v.title, excludes || [])) || null
      );
    }

    _currentOption() {
      return this.options.find((o) => o.id === this.optionId) || this._optionsForOrient()[0] || this.options[0] || null;
    }

    _resolved() {
      const opt = this._currentOption();
      const base = opt ? this._findVariant(opt.baseContains, opt.baseExcludes) : null;
      const print = opt ? this._findVariant(opt.printContains, []) : null;
      return { opt, base, print };
    }

    _setFront(file) {
      this.fileFront = file || null;
      if (this._frontUrl) URL.revokeObjectURL(this._frontUrl);
      this._frontUrl = file && file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
      if (this.fileFrontInput && !file) this.fileFrontInput.value = '';
      this._sync();
    }

    _setBack(file) {
      this.fileBack = file || null;
      if (this._backUrl) URL.revokeObjectURL(this._backUrl);
      this._backUrl = file && file.type?.startsWith('image/') ? URL.createObjectURL(file) : null;
      if (this.fileBackInput && !file) this.fileBackInput.value = '';
      this._sync();
    }

    _setStatus(msg, tone) {
      if (!this.statusEl) return;
      if (!msg) {
        this.statusEl.hidden = true;
        this.statusEl.textContent = '';
        return;
      }
      this.statusEl.hidden = false;
      this.statusEl.textContent = msg;
      this.statusEl.dataset.tone = tone || 'success';
    }

    _sync() {
      const i18n = this.i18n;
      const orientOptions = this._optionsForOrient();
      if (orientOptions.length && !orientOptions.some((o) => o.id === this.optionId)) {
        this.optionId = orientOptions[0].id;
      }
      const { opt, base, print } = this._resolved();

      this.orientPills?.querySelectorAll('[data-hb-orient]').forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-hb-orient') === this.orientId ? 'true' : 'false');
      });
      this.sizePills?.querySelectorAll('[data-hb-size]').forEach((btn) => {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-hb-size') === this.optionId ? 'true' : 'false');
      });

      if (this.descEl) this.descEl.innerHTML = opt?.description || '';

      if (this.printBtn) this.printBtn.setAttribute('aria-pressed', this.wantPrint ? 'true' : 'false');
      if (this.printMeta) {
        this.printMeta.textContent = this.wantPrint
          ? this._printBillingMeta(i18n)
          : `${t(i18n, 'printSizeLabel', 'Baskı alanı')}: ${opt?.printArea || ''}`;
      }
      if (this.printPriceEl) {
        this.printPriceEl.textContent = this.wantPrint && print ? `+ ${moneyOf(print)}` : '—';
      }
      if (this.uploadEl) this.uploadEl.hidden = !this.wantPrint;
      this.printSrc?.sync(this.wantPrint);
      if (this.printGuideEl) {
        const guideUrl = opt?.printGuideUrl || '';
        if (guideUrl) {
          this.printGuideEl.href = guideUrl;
          this.printGuideEl.hidden = false;
        } else {
          this.printGuideEl.removeAttribute('href');
          this.printGuideEl.hidden = true;
        }
      }

      if (this.printWarning) this.printWarning.hidden = true;

      if (this.fileFrontName) {
        this.fileFrontName.hidden = !this.fileFront;
        this.fileFrontName.textContent = this.fileFront?.name || '';
      }
      if (this.fileBackName) {
        this.fileBackName.hidden = !this.fileBack;
        this.fileBackName.textContent = this.fileBack?.name || '';
      }

      const baseCents = Number(base?.price) || 0;
      const printCents = this.wantPrint ? Number(print?.price) || 0 : 0;
      if (this.basePriceEl) this.basePriceEl.textContent = moneyOf(base);
      if (this.printRow) {
        this.printRow.classList.toggle('is-added', this.wantPrint);
        if (this.printStateEl) {
          this.printStateEl.textContent = this.wantPrint
            ? this._printBillingMeta(i18n)
            : t(i18n, 'addonNotAdded', 'Eklenmedi');
        }
        if (this.printLine) {
          this.printLine.textContent = this.wantPrint && print ? `+ ${moneyOf(print)}` : '—';
        }
      }
      if (this.totalEl) this.totalEl.textContent = formatMoney(baseCents + printCents);

      if (base?.id) {
        const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
        if (input) {
          input.value = String(base.id);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }


    _hasAnyPrintFile() {
      return Boolean(this.fileFront || this.fileBack);
    }

    _printBillingMeta(i18n) {
      if (this.printSrc?.isService) {
        return t(i18n, 'printSourceServiceValue', 'HarfMix tasarım / baskı hizmeti');
      }
      const both = Boolean(this.fileFront && this.fileBack);
      const one = Boolean((this.fileFront || this.fileBack) && !both);
      if (one) {
        return t(
          i18n,
          'printDualOneSide',
          'Tek yüz yüklendi — yine de 2 adet (çift yüz) ücreti uygulanır'
        );
      }
      return t(i18n, 'printOn', 'Eklendi — 2 adet (çift yüz) ücreti');
    }

    _printSidesSummary(i18n) {
      if (this.fileFront && this.fileBack) {
        return t(i18n, 'printSidesBoth', 'Ön + arka yüz');
      }
      if (this.fileFront) {
        return t(
          i18n,
          'printSidesFrontOnly',
          'Yalnızca ön yüz — 2 adet (çift yüz) ücreti uygulandı'
        );
      }
      if (this.fileBack) {
        return t(
          i18n,
          'printSidesBackOnly',
          'Yalnızca arka yüz — 2 adet (çift yüz) ücreti uygulandı'
        );
      }
      return '';
    }

    async _addToCart() {
      const i18n = this.i18n;
      const { opt, base, print } = this._resolved();
      if (!base?.id || !base.available) {
        this._setStatus(t(i18n, 'variantMissing', 'Varyant bulunamadı.'), 'error');
        return;
      }
      const isService = Boolean(this.printSrc?.isService);
      if (this.wantPrint && !isService && !this._hasAnyPrintFile()) {
        if (this.printWarning) this.printWarning.hidden = false;
        this._openPrintUpload();
        this._setStatus(
          t(i18n, 'printRequired', 'En az bir yüz için baskı dosyası yükleyin veya HarfMix hizmetini seçin.'),
          'error'
        );
        return;
      }
      if (this.wantPrint && !print?.id) {
        this._setStatus(t(i18n, 'variantMissing', 'Baskı varyantı bulunamadı.'), 'error');
        return;
      }

      const fd = new FormData();
      let index = 0;
      const appendItem = (variantId, props, files) => {
        fd.append(`items[${index}][id]`, String(variantId));
        fd.append(`items[${index}][quantity]`, '1');
        Object.entries(props).forEach(([k, v]) => {
          if (v == null || v === '') return;
          fd.append(`items[${index}][properties][${k}]`, String(v));
        });
        (files || []).forEach(({ key, file }) => {
          if (!file) return;
          fd.append(`items[${index}][properties][${key}]`, file, file.name);
        });
        index += 1;
      };

      appendItem(base.id, {
        [t(i18n, 'propType', 'Ürün Tipi')]: 'Askılı Lightbox',
        [t(i18n, 'propOrient', 'Yön')]: opt?.orientLabel || opt?.orient || '',
        [t(i18n, 'propSize', 'Ölçü')]: opt?.label ? `${opt.label} — ${opt.sizeLabel || ''}` : '',
        [t(i18n, 'propPrint', 'Görsel Baskı')]: this.wantPrint ? t(i18n, 'yes', 'Evet') : t(i18n, 'no', 'Hayır'),
      });

      if (this.wantPrint && print?.id) {
        const note = this.printSrc?.noteValue() || '';
        const printProps = {
          [t(i18n, 'propType', 'Ürün Tipi')]: 'Özel Dijital Baskı (2 adet)',
          [t(i18n, 'propSize', 'Ölçü')]: opt?.printArea || '',
          [t(i18n, 'propPrint', 'Görsel Baskı')]: t(i18n, 'yes', 'Evet'),
          [t(i18n, 'propPrintSource', 'Baskı tercihi')]: isService
            ? t(i18n, 'printSourceServiceValue', 'HarfMix tasarım / baskı hizmeti')
            : t(i18n, 'printSourceUploadValue', 'Dosya yükleme'),
          [t(i18n, 'propPrintBilling', 'Baskı ücreti')]: t(
            i18n,
            'printBillingTwo',
            '2 adet — çift yüz (tek yüz yüklense bile)'
          ),
          _hb_addon: 'print',
        };
        if (!isService) {
          printProps[t(i18n, 'propPrintSides', 'Yüklenen yüzler')] = this._printSidesSummary(i18n);
        }
        if (note) {
          printProps[t(i18n, 'propPrintNote', 'Baskı notu')] = note;
        }
        appendItem(
          print.id,
          printProps,
          isService
            ? []
            : [
                { key: t(i18n, 'propPrintFront', 'Ön Yüz Baskı'), file: this.fileFront },
                { key: t(i18n, 'propPrintBack', 'Arka Yüz Baskı'), file: this.fileBack },
              ]
        );
      }

      const sectionIds = cartSectionIds();
      if (sectionIds.length) fd.append('sections', sectionIds.join(','));

      this._setStatus(t(i18n, 'adding', 'Ekleniyor…'), 'success');
      try {
        const res = await fetch(cartAddUrl(), {
          method: 'POST',
          body: fd,
          headers: { Accept: 'application/json' },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || t(i18n, 'addError', 'Sepete eklenemedi.'));
        }
        this._setStatus(t(i18n, 'added', 'Sepete eklendi.'), 'success');
        const lines = [{ merchandiseId: String(base.id), quantity: 1 }];
        if (this.wantPrint && print?.id) lines.push({ merchandiseId: String(print.id), quantity: 1 });
        await notifyHorizonCart(data, lines);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data, bubbles: true }));
      } catch (err) {
        console.error('[HangboxCraft]', err);
        this._setStatus(err.message || t(i18n, 'addError', 'Sepete eklenemedi.'), 'error');
      }
    }
  }

  if (!customElements.get('hangbox-craft')) {
    customElements.define('hangbox-craft', HangboxCraft);
  }
})();
